import React from 'react';
import { Tooltip, Typography, Chip, Box, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import CELL_DATA_TYPES from '../../config/cell-types.config';
import {
  formatNumber,
  formatPercent,
  formatDuration,
  formatCurrency,
} from '../../utils/numbers.util';
import { formatDate, formatTimestamp } from '../../utils/datetime.util';
import StatusLabel from './status-label.component';
import PlatformLabel from './platform-label.component';
import Iconify from '../common/iconify.component';
import { APP_THEME } from '../../theme/theme-colors';
import ContactStatusLabel from './contact-status-label.component';
import { SESSION_TYPE } from '../../config/const.config';
import MP3StatusLabel from './mp3-status-label.component';
import EmailCampaignLabel from './email-campaign-label.component';
import EmailCampaignRecipientsLabel from './email-campaign-recipients-label.component';
import AssetSummaryLabel from './asset-summary-label.component';
import SwitchCell from './switch-cell.component';

const CellValue = ({
  type,
  value,
  color,
  fallbackValue,
  cdnStatus,
  disabledToggle,
}) => {
  if (type === CELL_DATA_TYPES.NUMBER) {
    return (
      <Typography variant="body2" component="span" color={color}>
        {formatNumber(value || 0)}
      </Typography>
    );
  }

  if (type === CELL_DATA_TYPES.PERCENTAGE) {
    return (
      <Tooltip title={value || 0} arrow>
        <Typography variant="body2" component="span" color={color}>
          {formatPercent(value || 0)}
        </Typography>
      </Tooltip>
    );
  }

  if (type === CELL_DATA_TYPES.MONEY) {
    return (
      <Tooltip title={value || '0'} arrow>
        <Typography variant="body2" component="span" color={color}>
          {formatCurrency(value || 0)}
        </Typography>
      </Tooltip>
    );
  }

  if (type === CELL_DATA_TYPES.DURATION) {
    return (
      <Tooltip title={`${value || 0} seconds`} arrow>
        <Typography variant="body2" component="span" color={color}>
          {formatDuration(value || 0)}
        </Typography>
      </Tooltip>
    );
  }

  if (type === CELL_DATA_TYPES.DATE) {
    return (
      <Tooltip title={value || ''} arrow>
        <Typography variant="body2" component="span" color={color}>
          {formatDate(value || null, 'll')}
        </Typography>
      </Tooltip>
    );
  }

  if (type === CELL_DATA_TYPES.DATETIME) {
    return (
      <Tooltip title={value || ''} arrow>
        <Typography variant="body2" component="span" color={color}>
          {formatDate(value || null, 'lll')}
        </Typography>
      </Tooltip>
    );
  }

  if (type === CELL_DATA_TYPES.TIMESTAMP) {
    return (
      <Tooltip title={value || ''} arrow>
        <Typography variant="body2" component="span" color={color}>
          {formatTimestamp(value || null, 'lll')}
        </Typography>
      </Tooltip>
    );
  }

  if (type === CELL_DATA_TYPES.BOOL) {
    return (
      <Chip
        label={Number(value) === 1 ? 'Yes' : 'No'}
        color={Number(value) === 1 ? 'success' : 'error'}
        variant="filled"
        size="small"
      />
    );
  }

  if (type === CELL_DATA_TYPES.PLATFORM) {
    return <PlatformLabel value={value} />;
  }

  if (type === CELL_DATA_TYPES.SWITCH) {
    return (
      <SwitchCell
        value={value}
        fallbackValue={fallbackValue}
        disabledToggle={disabledToggle}
      />
    );
  }

  if (type === CELL_DATA_TYPES.STATUS) {
    return <StatusLabel value={value} />;
  }

  if (type === CELL_DATA_TYPES.CONTACT_STATUS) {
    return <ContactStatusLabel value={value} />;
  }

  if (type === CELL_DATA_TYPES.MP3_STATUS) {
    return <MP3StatusLabel value={value} />;
  }

  if (type === CELL_DATA_TYPES.CODE) {
    return (
      <Box component="span" sx={{ display: 'block' }}>
        {value || ''}
      </Box>
    );
  }

  if (type === CELL_DATA_TYPES.PREVIEW) {
    return (
      <IconButton
        aria-label="preview"
        disabled={cdnStatus !== 'Finished'}
        sx={{ padding: 0, color: APP_THEME }}
      >
        <Iconify icon="solar:play-bold" />
      </IconButton>
    );
  }

  if (type === CELL_DATA_TYPES.IMAGE) {
    return (
      <Box
        component={value ? 'img' : 'div'}
        sx={{
          width: 80,
          aspectRatio: '16 / 9',
          bgcolor: value ? 'transparent' : '#919EAB',
          borderRadius: 1,
          objectFit: 'cover',
          display: 'block',
          margin: 'auto',
        }}
        src={value || undefined}
      />
    );
  }

  if (type === CELL_DATA_TYPES.SESSION_TYPE) {
    return (
      <Typography variant="body2" component="span" color={color}>
        {SESSION_TYPE[value] || ''}
      </Typography>
    );
  }

  if (type === CELL_DATA_TYPES.EMAIL_CAMPAIGN_STATUS) {
    return <EmailCampaignLabel value={value} />;
  }

  if (type === CELL_DATA_TYPES.EMAIL_CAMPAIGN_RECIPIENTS_STATUS) {
    return <EmailCampaignRecipientsLabel value={value} />;
  }

  if (type === CELL_DATA_TYPES.ASSET_SUMMARY) {
    return <AssetSummaryLabel value={value} />;
  }

  return (
    <Typography variant="body2" component="span" color={color}>
      {value || ''}
    </Typography>
  );
};

CellValue.propTypes = {
  type: PropTypes.string.isRequired,
  value: PropTypes.any,
  color: PropTypes.string,
  fallbackValue: PropTypes.func,
  cdnStatus: PropTypes.string,
  disabledToggle: PropTypes.bool,
};
CellValue.defaultProps = {
  value: null,
  color: 'inherit',
  fallbackValue: () => {},
  cdnStatus: null,
  disabledToggle: false,
};

export default CellValue;
